import { Component, computed, effect, inject, input, OnInit, output } from '@angular/core';
import {
  MAT_DATE_RANGE_SELECTION_STRATEGY,
  MatDatepickerInputEvent,
  MatDateRangeInput,
  MatDateRangePicker,
  MatEndDate,
  MatStartDate,
} from '@angular/material/datepicker';

import { DateService } from '../../services/date.service';
import { isPreviousDateNavigationDisabled } from '../../utils/date-range.utils';
import { CustomRangeSelectionStrategy } from '../disease-overview-table.component/strategies/custom-range-selection.strategy';
import { DateSelectorStore } from './date-selector.store';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'app-date-selector',
  standalone: true,
  templateUrl: './date-selector.component.html',
  styleUrl: './date-selector.component.scss',
  providers: [
    { provide: MAT_DATE_RANGE_SELECTION_STRATEGY, useFactory: () => new CustomRangeSelectionStrategy<Date>(11) },
    DateSelectorStore,
  ],
  imports: [
    MatFormFieldModule,
    MatDateRangeInput,
    MatDateRangePicker,
    MatIconModule,
    FormsModule,
    MatStartDate,
    MatEndDate,
  ],
})
export class DateSelectorComponent implements OnInit {
  protected readonly dateStore = inject(DateSelectorStore);
  private readonly dateService = inject(DateService);
  private hasInitialized = false;

  public readonly minimalDate = input<Date | undefined>(undefined);
  public readonly maximalDate = input.required<Date>();

  public readonly selectedDates = input<Date[]>([]);
  public readonly cropYear = input<number | undefined>(undefined);

  public readonly daysInPast = input<number>(5);
  public readonly daysInFuture = input<number>(5);

  public readonly selectedDatesChanges = output<Date[]>();
  public readonly dateNavigated = output<'back' | 'forward'>();

  constructor() {
    effect(() => {
      const dates = this.selectedDates();
      if (!this.hasInitialized || dates.length === 0) {
        return;
      }

      this.dateStore.initializeDates(dates[0], dates[dates.length - 1]);
    });
  }

  protected readonly previousButtonDisabled = isPreviousDateNavigationDisabled(
    this.dateStore.selectedDates,
    this.minimalDate,
    this.dateService,
  );

  protected readonly nextButtonDisabled = computed(() => {
    const currentDates = this.dateStore.selectedDates();
    const max = this.maximalDate();
    return (
      currentDates.length === 0 || this.dateService.isDateLaterThanOrEquals(currentDates.at(-1)!, max)
    );
  });

  public ngOnInit(): void {
    const providedDates = this.selectedDates();

    if (providedDates.length > 0) {
      this.dateStore.initializeDates(providedDates[0], providedDates[providedDates.length - 1]);
    } else {
      const cropYear = this.cropYear();
      const defaultRange =
        cropYear === undefined
          ? this.dateService.generateDateRange(this.daysInPast(), this.daysInFuture())
          : this.dateService.generateDateRange(this.daysInPast(), this.daysInFuture(), cropYear);
      this.dateStore.initializeDates(defaultRange[0], defaultRange[defaultRange.length - 1]);
    }

    this.selectedDatesChanges.emit(this.dateStore.selectedDates());
    this.hasInitialized = true;
  }

  protected onPreviousDay(): void {
    this.dateStore.moveDatesByOneDay(-1);
    this.selectedDatesChanges.emit(this.dateStore.selectedDates());
    this.dateNavigated.emit('back');
  }

  protected onNextDay(): void {
    this.dateStore.moveDatesByOneDay(1);
    this.selectedDatesChanges.emit(this.dateStore.selectedDates());
    this.dateNavigated.emit('forward');
  }

  protected onStartPickerChange(event: MatDatepickerInputEvent<Date>): void {
    this.dateStore.updateStartDate(event.value);
    this.selectedDatesChanges.emit(this.dateStore.selectedDates());
  }

  protected onEndPickerChange(event: MatDatepickerInputEvent<Date>): void {
    this.dateStore.updateEndDate(event.value);
    this.selectedDatesChanges.emit(this.dateStore.selectedDates());
  }
}
